import React, { useRef } from 'react';
import type { Graph, Node } from '@antv/x6';
import { calculateProcessSize } from '../utils/sizing';

interface FloatingToolbarProps {
  graph: Graph | null;
  simulationRunning: boolean;
  onStartSimulation: () => void;
  onStopSimulation: () => void;
  onStartDataFlow: () => void;
  onStopDataFlow: () => void;
  dataFlowActive: boolean;
}

const PROCESS_CHILD_PADDING = { top: 60, right: 50, bottom: 60, left: 50 };

const expandProcessParentBounds = (parentNode: Node | null | undefined) => {
  if (!parentNode) return;
  const parentData = parentNode.getData?.();
  if (parentData?.type !== 'process') return;

  const parentLabel = parentData?.label || 'Process';
  const minSize = calculateProcessSize(parentLabel);

  parentNode.fit?.({ padding: PROCESS_CHILD_PADDING });
  const parentSize = parentNode.getSize();
  if (parentSize.width < minSize.width || parentSize.height < minSize.height) {
    parentNode.setSize({
      width: Math.max(parentSize.width, minSize.width),
      height: Math.max(parentSize.height, minSize.height),
    });
  }

  const children = parentNode.getChildren?.()?.filter((child: any) => child?.isNode?.()) || [];
  if (!children.length) return;

  const parentPos = parentNode.getPosition();
  const size = parentNode.getSize();
  const centerX = parentPos.x + size.width / 2;
  const centerY = parentPos.y + size.height / 2;
  let rx = size.width / 2;
  let ry = size.height / 2;
  const aspect = ry === 0 ? 1 : rx / ry;
  let needExpand = false;

  children.forEach((child: any) => {
    const cp = child.getPosition();
    const cs = child.getSize();
    const corners = [
      { x: cp.x, y: cp.y },
      { x: cp.x + cs.width, y: cp.y },
      { x: cp.x, y: cp.y + cs.height },
      { x: cp.x + cs.width, y: cp.y + cs.height },
    ];
    corners.forEach(({ x, y }) => {
      const ddx = x - centerX;
      const ddy = y - centerY;
      const dist = rx === 0 || ry === 0 ? Infinity : (ddx * ddx) / (rx * rx) + (ddy * ddy) / (ry * ry);
      if (dist > 1) {
        needExpand = true;
        const newRy = Math.sqrt(Math.max(0, (ddx * ddx) / (aspect * aspect) + ddy * ddy));
        const newRx = aspect * newRy;
        if (newRx > rx) {
          rx = newRx;
          ry = newRy;
        }
      }
    });
  });

  if (needExpand) {
    parentNode.setPosition(centerX - rx, centerY - ry);
    parentNode.setSize({ width: rx * 2, height: ry * 2 });
  }
};

const FloatingToolbar: React.FC<FloatingToolbarProps> = ({
  graph,
  simulationRunning,
  onStartSimulation,
  onStopSimulation,
  onStartDataFlow,
  onStopDataFlow,
  dataFlowActive,
}) => {
  const dragNodeRef = useRef<'object' | 'process' | 'option' | null>(null);
  const graphRef = useRef(graph);
  graphRef.current = graph;
  const dropHandlerRef = useRef<((e: DragEvent) => void) | null>(null);

  const addNode = (type: 'object' | 'process' | 'state' | 'value' | 'option') => {
    if (!graph) return;

    const center = graph.clientToLocal(
      graph.container.clientWidth / 2,
      graph.container.clientHeight / 2,
    );

    // 添加小幅随机偏移，避免节点重叠
    const offsetX = (Math.random() - 0.5) * 80;
    const offsetY = (Math.random() - 0.5) * 80;

    const shapeMap = {
      object: 'opm-object',
      option: 'opm-option',
      process: 'opm-process',
      state: 'opm-state',
      value: 'opm-value',
    };

    const labelMap = {
      object: 'New Attribute',
      option: 'opm-attribute',
      process: 'New Process',
      state: 'New State',
      value: 'New Value',
    };

    // 不同形状使用不同的默认尺寸
    const sizeMap: Record<string, { width: number; height: number }> = {
      object: { width: 180, height: 80 },
      option: { width: 180, height: 80 },
      process: { width: 140, height: 100 },
      state: { width: 80, height: 60 },
      value: { width: 70, height: 30 },
    };

    const size = sizeMap[type];

    graph.addNode({
      shape: shapeMap[type],
      width: size.width,
      height: size.height,
      x: center.x + offsetX - size.width / 2,
      y: center.y + offsetY - size.height / 2,
      data: {
        label: labelMap[type],
        type,
        essence: 'informational',
        affiliation: 'systemic',
        active: false,
        executionState: 'idle',
        childStateCount: 0,
      },
    });
  };

  // 从工具栏按钮开始拖拽
  const handleDragStart = (
    e: React.DragEvent,
    type: 'object' | 'process' | 'option',
  ) => {
    dragNodeRef.current = type;
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', type);
    // 创建小型拖拽预览图像
    const img = new Image();
    img.src =
      type === 'object'
        ? 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><rect width="20" height="20" rx="3" fill="%2370E483" stroke="%2352c41a" stroke-width="2"/></svg>'
        : type === 'option'
          ? 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><rect width="20" height="20" rx="3" fill="%2370E483" stroke="%2352c41a" stroke-width="2"/><line x1="10" y1="0" x2="10" y2="-6" stroke="%23595959" stroke-width="2"/></svg>'
          : 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><ellipse cx="10" cy="10" rx="9" ry="9" fill="%233BC3FF" stroke="%231890ff" stroke-width="2"/></svg>';
    e.dataTransfer.setDragImage(img, 10, 10);
  };

  // 在画布上放置节点 — 使用 ref 保证闭包始终最新
  dropHandlerRef.current = (e: DragEvent) => {
    e.preventDefault();
    const g = graphRef.current;
    if (!g) return;

    // 仅在画布容器范围内才处理放置
    const rect = g.container.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right ||
        e.clientY < rect.top || e.clientY > rect.bottom) return;

    const type = dragNodeRef.current || (e.dataTransfer?.getData('text/plain') as any);
    dragNodeRef.current = null;
    if (!type || !['object', 'process', 'option', 'state', 'value'].includes(type)) return;

    const point = g.clientToLocal(e.clientX, e.clientY);

    const shapeMap: Record<string, string> = {
      object: 'opm-object',
      option: 'opm-option',
      process: 'opm-process',
      state: 'opm-state',
      value: 'opm-value',
    };

    const labelMap: Record<string, string> = {
      object: 'New Attribute',
      option: 'opm-attribute',
      process: 'New Process',
      state: 'New State',
      value: 'New Value',
    };

    const sizeMap: Record<string, { width: number; height: number }> = {
      object: { width: 180, height: 80 },
      option: { width: 180, height: 80 },
      process: { width: 140, height: 100 },
      state: { width: 80, height: 60 },
      value: { width: 70, height: 30 },
    };

    const size = sizeMap[type];

    // 仅在子图中，放置点在父 process 节点副本（isParentCopy）内时才嵌入为子节点
    const subGraphCtx = (window as any).__OPM_SUBGRAPH__;
    const isSubGraph = subGraphCtx?.currentGraphId !== 'root';
    const containerNode = isSubGraph
      ? g.getNodes().find((n: any) => {
          const nData = n.getData();
          if (!nData?.isParentCopy || nData?.type !== 'process') return false;
          const pos = n.getPosition();
          const sz = n.getSize();
          return (
            point.x >= pos.x &&
            point.x <= pos.x + sz.width &&
            point.y >= pos.y &&
            point.y <= pos.y + sz.height
          );
        })
      : undefined;

    const newNode = g.addNode({
      shape: shapeMap[type],
      width: size.width,
      height: size.height,
      x: point.x - size.width / 2,
      y: point.y - size.height / 2,
      data: {
        label: labelMap[type],
        type,
        essence: 'informational',
        affiliation: 'systemic',
        active: false,
        executionState: 'idle',
        childStateCount: 0,
        ...(containerNode ? { parentId: containerNode.id } : {}),
      },
    });

    if (containerNode) {
      containerNode.embed(newNode);
      containerNode.toBack();
      newNode.toFront();
      containerNode.attr('body/pointerEvents', 'none');
      expandProcessParentBounds(containerNode);
    }
  };

  // 监听 document 级别的拖拽事件，绕过 X6 内部事件拦截
  React.useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      // 只在画布容器范围内允许放置
      const g = graphRef.current;
      if (!g) return;
      const rect = g.container.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      }
    };
    const onDrop = (e: DragEvent) => {
      dropHandlerRef.current?.(e);
    };
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
    };
  }, []);

  const resetView = () => {
    if (!graph) return;
    graph.zoomToFit({ padding: 40, maxScale: 1.5 });
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        background: 'rgba(255,255,255,0.96)',
        borderRadius: 10,
        padding: '8px 6px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(0,0,0,0.06)',
        transform: 'none',
        WebkitTransform: 'none',
      }}
    >
      {/* Section: Node Types */}
      <div style={{ fontSize: 10, color: '#999', fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase', transform: 'none', WebkitTransform: 'none' }}>
        节点
      </div>

      <ToolbarBtn
        label="Object"
        color="#70E483"
        shape="rect"
        onClick={() => addNode('object')}
        onDragStart={(e) => handleDragStart(e, 'object')}
        draggable
        title="拖拽到画布创建 Object"
      />
      <ToolbarBtn
        label="Attribute"
        color="#70E483"
        shape="rect"
        onClick={() => addNode('option')}
        onDragStart={(e) => handleDragStart(e, 'option')}
        draggable
        title="拖拽到画布创建 Attribute"
      />
      <ToolbarBtn
        label="Process"
        color="#3BC3FF"
        shape="circle"
        onClick={() => addNode('process')}
        onDragStart={(e) => handleDragStart(e, 'process')}
        draggable
        title="拖拽到画布创建 Process"
      />
      {/* <ToolbarBtn
        label="State"
        color="#fa8c16"
        shape="roundrect"
        onClick={() => addNode('state')}
        title="点击创建 State"
      />
      <ToolbarBtn
        label="Value"
        color="#4169E1"
        shape="roundrect"
        onClick={() => addNode('value')}
        title="点击创建 Value"
      /> */}

      <Divider />

      {/* Section: Simulation */}
      <div style={{ fontSize: 10, color: '#999', fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase', transform: 'none', WebkitTransform: 'none' }}>
        仿真
      </div>

      <SimBtn
        label="数据流"
        active={dataFlowActive}
        onClick={dataFlowActive ? onStopDataFlow : onStartDataFlow}
        icon="⟿"
      />
      <SimBtn
        label="执行模拟"
        active={simulationRunning}
        onClick={simulationRunning ? onStopSimulation : onStartSimulation}
        icon="▶"
      />

      <Divider />

      {/* Section: Data */}
      <div style={{ fontSize: 10, color: '#999', fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase', transform: 'none', WebkitTransform: 'none' }}>
        数据
      </div>

      <button
        onClick={() => {
          if (!graph) return;
          const nodes = graph.getNodes().map(n => {
            const pos = n.getPosition();
            const size = n.getSize();
            const parent = n.getParent();
            return {
              id: n.id,
              shape: n.shape,
              x: pos.x,
              y: pos.y,
              width: size.width,
              height: size.height,
              parentId: parent?.id || null,
              data: { ...n.getData() },
            };
          });

          const edges = graph.getEdges().map(e => {
            const source = e.getSource();
            const target = e.getTarget();
            return {
              id: e.id,
              shape: e.shape,
              source: typeof source === 'string' ? { cell: source } : source,
              target: typeof target === 'string' ? { cell: target } : target,
              data: { ...e.getData() },
              attrs: e.getAttrs(),
              vertices: e.getVertices(),
            };
          });

          console.log('=== OPM Graph Data ===');
          console.log('Nodes:', JSON.stringify(nodes, null, 2));
          console.log('Edges:', JSON.stringify(edges, null, 2));
          console.log(`Total: ${nodes.length} nodes, ${edges.length} edges`);
          console.table(nodes.map(n => ({ id: n.id, shape: n.shape, label: n.data?.label, x: Math.round(n.x), y: Math.round(n.y), parentId: n.parentId })));
        }}
        style={{ ...iconBtnStyle, justifyContent: 'start', gap: 4, width: '100%', lineHeight:'14', transition: 'background 0.15s', fontSize: 12,color: '#434343', fontWeight: 500, transform: 'none', WebkitTransform: 'none' }}
        title="打印图数据到控制台"
      > 
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
          <polyline points="17 21 17 13 7 13 7 21" />
          <polyline points="7 3 7 8 15 8" />
        </svg>
        保存
      </button>

      <Divider />

      {/* Section: View */}
      <div style={{ fontSize: 10, color: '#999', fontWeight: 600, padding: '2px 6px', textTransform: 'uppercase', transform: 'none', WebkitTransform: 'none' }}>
        视图
      </div>

      <button
        onClick={() => graph?.zoom(0.15)}
        style={{ ...iconBtnStyle, transform: 'none', WebkitTransform: 'none' }}
        title="放大"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      </button>
      <button
        onClick={() => graph?.zoom(-0.15)}
        style={{ ...iconBtnStyle, transform: 'none', WebkitTransform: 'none' }}
        title="缩小"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      </button>
      <button onClick={resetView} style={{ ...iconBtnStyle, transform: 'none', WebkitTransform: 'none' }} title="适应画布">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
        </svg>
      </button>
    </div>
  );
};

/* ---- 辅助组件 ---- */

const Divider = () => (
  <div style={{ height: 1, background: '#f0f0f0', margin: '2px 0', transform: 'none', WebkitTransform: 'none' }} />
);

interface ToolbarBtnProps {
  label: string;
  color: string;
  shape: 'rect' | 'circle' | 'roundrect';
  onClick: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  draggable?: boolean;
  title?: string;
}

const ToolbarBtn: React.FC<ToolbarBtnProps> = ({
  label,
  color,
  shape,
  onClick,
  onDragStart,
  draggable,
  title,
}) => {
  const shapeStyle: React.CSSProperties =
    shape === 'circle'
      ? { width: 14, height: 14, borderRadius: '50%', transform: 'none', WebkitTransform: 'none' }
      : shape === 'roundrect'
      ? { width: 14, height: 14, borderRadius: 4, transform: 'none', WebkitTransform: 'none' }
      : { width: 14, height: 14, borderRadius: 2, transform: 'none', WebkitTransform: 'none' };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onDragStart={onDragStart}
      draggable={draggable}
      title={title}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 8px',
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        cursor: draggable ? 'grab' : 'pointer',
        transition: 'background 0.15s',
        fontSize: 12,
        color: '#434343',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        transform: 'none',
        WebkitTransform: 'none',
        outline: 'none',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span
        style={{
          ...shapeStyle,
          background: color,
          boxShadow: `0 0 6px ${color}40`,
        }}
      />
      {label}
    </div>
  );
};

interface SimBtnProps {
  label: string;
  active: boolean;
  onClick: () => void;
  icon: string;
}

const SimBtn: React.FC<SimBtnProps> = ({ label, active, onClick, icon }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: '5px 8px',
      border: 'none',
      borderRadius: 6,
      background: active ? '#e6f7ff' : 'transparent',
      cursor: 'pointer',
      transition: 'background 0.15s',
      fontSize: 12,
      color: active ? '#1890ff' : '#434343',
      fontWeight: active ? 600 : 500,
      transform: 'none',
      WebkitTransform: 'none',
    }}
    onMouseEnter={(e) => {
      if (!active) e.currentTarget.style.background = '#f5f5f5';
    }}
    onMouseLeave={(e) => {
      if (!active) e.currentTarget.style.background = 'transparent';
    }}
  >
    <span style={{ fontSize: 14, transform: 'none', WebkitTransform: 'none' }}>{icon}</span>
    {label}
  </button>
);

const iconBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  cursor: 'pointer',
  color: '#595959',
  transition: 'background 0.15s',
};

export default FloatingToolbar;